import { useId } from "react";
import { Shield } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";

const CX = 110;
const CY = 118;
const R = 86;
const STROKE = 18;

const HatchGauge = ({ value, total, loading }) => {
  const uid = useId().replace(/:/g, "");
  const hatchId = `anon-hatch-${uid}`;
  const maskId = `anon-mask-${uid}`;
  const ratio = !loading && total > 0 ? value / total : 0;
  const arcLen = Math.PI * R;
  const progressLen = arcLen * ratio;
  const displayValue = loading ? "—" : value;
  const displayTotal = loading ? "—" : total;

  return (
    <div className="relative mx-auto w-full max-w-[132px]">
      <svg
        viewBox="0 0 220 148"
        className="h-auto w-full"
        role="img"
        aria-label={`${displayValue} of ${displayTotal} patients anonymized`}
      >
        <defs>
          <pattern
            id={hatchId}
            width="7"
            height="7"
            patternUnits="userSpaceOnUse"
            patternTransform="rotate(42)"
          >
            <line
              x1="0"
              y1="0"
              x2="0"
              y2="7"
              stroke="#E8A090"
              strokeWidth="2.2"
            />
          </pattern>
          <mask id={maskId}>
            <path
              d={`M ${CX - R} ${CY} A ${R} ${R} 0 0 1 ${CX + R} ${CY}`}
              fill="none"
              stroke="white"
              strokeWidth={STROKE}
              strokeLinecap="butt"
              strokeDasharray={`${Math.max(0, arcLen - progressLen)} ${arcLen}`}
              strokeDashoffset={-progressLen}
            />
          </mask>
        </defs>
        <rect
          x="0"
          y="0"
          width="220"
          height="148"
          fill={`url(#${hatchId})`}
          mask={`url(#${maskId})`}
        />
        <path
          d={`M ${CX - R} ${CY} A ${R} ${R} 0 0 1 ${CX + R} ${CY}`}
          fill="none"
          stroke="#1A1A2E"
          strokeWidth={STROKE}
          strokeLinecap="round"
          strokeDasharray={`${progressLen} ${arcLen}`}
        />
      </svg>
      <div className="pointer-events-none absolute inset-x-0 bottom-[8%] flex flex-col items-center">
        <span className="text-3xl font-bold leading-none tracking-tight text-[#1A1A2E] tabular-nums">
          {displayValue}
        </span>
        <span className="mt-1 text-sm font-medium text-[#9A6558]">
          of {displayTotal}
        </span>
      </div>
    </div>
  );
};

const MetricTile = ({
  value,
  label,
  variant = "light",
  onClick,
  loading,
  testId,
}) => {
  const isClickable = typeof onClick === "function";
  const isDark = variant === "dark";

  const handleKeyDown = (e) => {
    if (!isClickable) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onClick();
    }
  };

  return (
    <div
      className={cn(
        "min-w-0 flex-1 rounded-2xl px-2.5 py-1.5 sm:px-3 sm:py-2",
        isDark ? "bg-[#1A1A2E] text-white" : "bg-white text-[#1A1A2E]",
        isClickable && "cursor-pointer select-none transition-transform hover:scale-[1.02] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1A1A2E]/40",
      )}
      role={isClickable ? "button" : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onClick={isClickable ? onClick : undefined}
      onKeyDown={isClickable ? handleKeyDown : undefined}
      aria-label={isClickable ? `View ${label} patients` : undefined}
      data-testid={testId}
    >
      <p className="text-xl font-bold leading-none tracking-tight tabular-nums sm:text-2xl">
        {loading ? "—" : value}
      </p>
      <p
        className={cn(
          "mt-1.5 text-[11px] font-medium leading-tight sm:text-xs",
          isDark ? "text-[#C9C4B8]" : "text-[#9A6558]",
        )}
      >
        {label}
      </p>
    </div>
  );
};

const AnonymizationCard = ({
  anonymized = 0,
  identified = 0,
  pending = 0,
  loading = false,
  delay = "",
  onAnonymizedClick,
  onIdentifiedClick,
  onPendingClick,
  className,
}) => {
  const total = Math.max(0, Number(anonymized) + Number(identified));

  return (
    <Card
      className={cn(
        "card-hover animate-fade-in h-full min-w-0 overflow-hidden border-0 shadow-none",
        delay,
        className,
      )}
      data-testid="dashboard-stat-anonymization"
    >
      <CardContent className="flex h-full flex-col bg-[#FDE2D9] p-3">
        <div className="flex items-start justify-between gap-2">
          <h3 className="text-sm font-bold tracking-tight text-[#1A1A2E] sm:text-base">
            Anonymization
          </h3>
          <button
            type="button"
            className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#1A1A2E] text-white transition-opacity hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1A1A2E]/40 disabled:opacity-100"
            onClick={onAnonymizedClick}
            disabled={!onAnonymizedClick}
            aria-label="View anonymized patients"
            data-testid="dashboard-stat-item-anonymized_patients"
          >
            <Shield className="h-3 w-3 text-white" aria-hidden />
          </button>
        </div>

        <button
          type="button"
          className="mt-0.5 w-full rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1A1A2E]/40"
          onClick={onAnonymizedClick}
          aria-label="View anonymized patients"
        >
          <HatchGauge value={Number(anonymized) || 0} total={total} loading={loading} />
        </button>

        <div className="mt-auto grid grid-cols-2 gap-2 pt-1">
          <MetricTile
            value={identified}
            label="fully identified"
            variant="light"
            loading={loading}
            onClick={onIdentifiedClick}
            testId="dashboard-stat-item-unanonymized_patients"
          />
          <MetricTile
            value={pending}
            label="pending"
            variant="dark"
            loading={loading}
            onClick={onPendingClick}
            testId="dashboard-stat-item-anonymization-pending"
          />
        </div>
      </CardContent>
    </Card>
  );
};

export default AnonymizationCard;
